import React from "react";
import type { CampaignBoost, CampaignKind, CampaignStatus, CampaignTier, PreviewResponse } from "../../../api/maintenance/campaigns";
import { tierBand, tierPays } from "./campaignUtils";

/** What the customer gets, beside the form that decides it.
 *
 *  The copy is NOT rebuilt here. It comes from the server, which runs the same
 *  _copy() the dispatcher sends with -- a preview that merely resembles the
 *  message is worse than no preview, because it is trusted and then wrong. The
 *  only thing this file decides is what the surfaces look like, which mirrors
 *  CashbackBanner.tsx in the app.
 */

interface Props {
  data: PreviewResponse | null;
  tiers: CampaignTier[];
  kind: CampaignKind;
  loading: boolean;
  /** The name is an internal handle; the banner leads with it. Shown so the
   *  operator sees what a customer sees before it ships. */
  name: string;
  headline: string;
  /** Drives the banner's verb. The app says "is coming" before a campaign
   *  opens and "is live" during it, so a preview that always said "is live"
   *  told a draft it was running. */
  status: CampaignStatus | null;
  /** The boost editor's theme, saved or not. */
  theme: CampaignBoost["theme"];
  /** Extra images in the Artwork card. The preview shows the main image only;
   *  which image a customer gets is the server's choice, not redone here. */
  otherImages?: number;
}

const MOMENT: Record<string, string> = {
  teaser: "Teaser — before it opens",
  announce: "Launch — when it goes live",
  reminder: "Reminder — before it closes",
};

const card: React.CSSProperties = {
  border: "1px solid #e6e8ee", borderRadius: 10, background: "#fff", padding: 12,
};
const label: React.CSSProperties = {
  fontSize: 10.5, letterSpacing: ".07em", textTransform: "uppercase",
  fontWeight: 700, color: "#69787f", marginBottom: 8,
};

const CampaignPreview: React.FC<Props> = ({ data, tiers, kind, loading, name, headline, status, theme, otherImages = 0 }) => {
  const img = data?.image || null;
  const isAnnouncement = kind === "announcement";
  // The app banner is fed by two queries that both filter `kind = 'offer'`, so
  // an announcement never reaches it. Drawing one here showed a surface that
  // does not exist. An announcement's surfaces are the push and the bell row.
  const live = status === "live";
  const bannerVerb = live ? "is live" : "is coming";
  const topRate = tiers.length
    ? Math.max(...tiers.map((t) => (t.is_random ? t.max_percentage ?? 0 : t.fixed_percentage ?? 0)))
    : null;

  return (
    <div className="ma-card" style={{ marginTop: 14 }}>
      <div className="ma-card-h">
        <h3>What the customer gets{theme === "diwali" && <span className="ma-pill info" style={{ marginLeft: 6 }}>Diwali theme</span>}</h3>
        <span className="ma-hint">{loading ? "updating…" : "updates as you type"}</span>
      </div>
      <div className="ma-card-b">
        {!data ? (
          <p className="ma-hint" style={{ margin: 0 }}>Give the campaign a name to see the preview.</p>
        ) : (<>
          {otherImages > 0 && (
            <p className="ma-hint" style={{ margin: "0 0 10px" }}>
              Shows the main image. {otherImages === 1 ? "1 other image replaces" : `${otherImages} other images replace`} it at set times or for some customers.
            </p>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 14 }}>

            {/* ---------------- the push, one card per moment --------------- */}
            <div>
              <div style={label}>
                Push notification
                {!data.channels.push && <span style={{ color: "#d03b3b", marginLeft: 6 }}>· push is off</span>}
              </div>
              {data.messages.map((m) => (
                <div key={m.kind} style={{ ...card, marginBottom: 9, opacity: data.channels.push ? 1 : 0.45 }}>
                  <div style={{ fontSize: 10.5, color: "#69787f", fontWeight: 650, marginBottom: 6 }}>{MOMENT[m.kind] ?? m.kind}</div>
                  <div style={{ display: "flex", gap: 9 }}>
                    <div style={{ width: 26, height: 26, borderRadius: 6, background: "#1baf7a", color: "#fff", display: "grid", placeItems: "center", fontSize: 13, flex: "0 0 auto" }}>⚡</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 11, color: "#69787f" }}>Charging Adda · now</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#10161c", lineHeight: 1.35 }}>{m.title}</div>
                      <div style={{ fontSize: 12, color: "#3c4a55", lineHeight: 1.4 }}>{m.body}</div>
                    </div>
                  </div>
                  {img && (
                    <img src={img} alt="" style={{ display: "block", width: "100%", maxHeight: 92, objectFit: "cover", borderRadius: 7, marginTop: 8, border: "1px solid #e6e8ee" }}
                      onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  )}
                </div>
              ))}
              {!data.channels.inapp && (
                <p className="ma-hint" style={{ margin: "2px 0 0" }}>The in-app bell is off, so a customer with no push token gets nothing at all.</p>
              )}
            </div>

            {/* -------- the second surface, which differs by kind ---------- */}
            <div>
              {isAnnouncement ? (
                <>
                  <div style={label}>In-app bell</div>
                  <div style={card}>
                    <div style={{ display: "flex", gap: 9 }}>
                      <div style={{ width: 26, height: 26, borderRadius: 6, background: "#1baf7a", color: "#fff", display: "grid", placeItems: "center", fontSize: 13, flex: "0 0 auto" }}>🔔</div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#10161c", lineHeight: 1.35 }}>{data.messages[0]?.title}</div>
                        <div style={{ fontSize: 12, color: "#3c4a55", lineHeight: 1.4 }}>{data.messages[0]?.body}</div>
                        <div style={{ fontSize: 11, color: "#69787f", marginTop: 3 }}>just now</div>
                      </div>
                    </div>
                  </div>
                  <p className="ma-hint" style={{ margin: "8px 0 0" }}>
                    An announcement has no in-app banner — only this bell row and the push.
                  </p>
                </>
              ) : (
                <>
                  <div style={label}>In-app banner <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>· {live ? "as it looks now" : "as it will look once it opens"}</span></div>
                  <div style={{ ...card, padding: 14 }}>
                    {img ? (
                      <img src={img} alt="" style={{ display: "block", width: "100%", maxHeight: 96, objectFit: "cover", borderRadius: 9, marginBottom: 10 }}
                        onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    ) : (
                      <div style={{ width: 42, height: 42, borderRadius: 12, background: "#1baf7a", display: "grid", placeItems: "center", color: "#fff", fontSize: 20, margin: "0 auto 10px" }}>🎁</div>
                    )}
                    <h4 style={{ margin: 0, textAlign: "center", fontSize: 16, color: "#10161c" }}>
                      {data.banner_title} {bannerVerb}
                    </h4>
                    <p style={{ margin: "5px 0 12px", textAlign: "center", fontSize: 12.5, color: "#3c4a55" }}>
                      {headline.trim() || (topRate ? `Up to ${topRate}% back on wallet top-ups` : "Open the app to see your cashback offer.")}
                    </p>
                    {tiers.length === 0 ? (
                      <p className="ma-hint" style={{ margin: 0, textAlign: "center" }}>Add a slot in step 2 to see the rate card.</p>
                    ) : (
                      tiers.map((t, i) => (
                        <div key={(t.name || "") + i} style={{ border: "1px solid #e6e8ee", borderLeft: "3px solid #1baf7a", borderRadius: 8, padding: "8px 11px", marginBottom: 7 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, fontWeight: 700 }}>
                            <span>{tierBand(t)}</span><span>{tierPays(t)}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {name.trim() && headline.trim() && name.trim() !== headline.trim() && (
                    <p className="ma-hint" style={{ margin: "8px 0 0" }}>
                      Customers see “{name.trim()}” here and “{headline.trim()}” in the push. Rename
                      the campaign if that first one is an internal handle.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </>)}
      </div>
    </div>
  );
};

export default CampaignPreview;
